import type { DataProvider } from '@refinedev/core';
import Config from 'src/common/Config';
import { ApiError } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import * as Sentry from '@sentry/react';
import { redirectToLogin } from 'src/auth/RedirectToLoginPage';

const API_URL = Config.getApiUrl();

export const dataProvider: DataProvider = {
  getOne: async ({ resource, id }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;
    return await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getOne',
    );
  },
  update: async ({ resource, id, variables }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;

    return await fetchWrapper(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(variables),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'update',
    );
  },
  getList: async ({ resource, filters, pagination }) => {
    let url = `${API_URL}/${resource}`;
    const queryParams = new URLSearchParams();

    if (filters) {
      filters.forEach(filter => {
        if ('field' in filter) {
          if (filter.operator === 'eq') {
            queryParams.append(filter.field, filter.value);
          } else {
            queryParams.append(`${filter.field}_${filter.operator}`, filter.value);
          }
        }
      });
    }

    if (pagination && pagination.mode === 'server') {
      if (pagination.current) {
        queryParams.append('page', pagination.current.toString());
      }
      if (pagination.pageSize) {
        queryParams.append('size', pagination.pageSize.toString());
      }
    }

    url = `${url}?${queryParams.toString()}`;
    const result = await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getList',
    );

    if (result.data && Array.isArray(result.data.items) && typeof result.data.total === 'number') {
      return {
        data: result.data.items,
        total: result.data.total,
      };
    }

    return {
      ...result,
      total: result?.data?.total ?? 0,
    };
  },
  create: async ({ resource, variables, meta }) => {
    const query = meta?.query ? `?${new URLSearchParams(meta.query).toString()}` : '';
    return await fetchWrapper(
      `${API_URL}/${resource}${query}`,
      {
        method: 'POST',
        body: JSON.stringify(variables ?? {}),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'create',
    );
  },
  deleteOne: async ({ resource, id }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/${id}`,
      {
        method: 'DELETE',
        credentials: 'include',
      },
      'deleteOne',
    );
  },
  deleteMany: async ({ resource, ids }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/delete-request`,
      {
        method: 'POST',
        body: JSON.stringify(ids),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'deleteMany',
    );
  },
  custom: async ({ url, method, headers, payload }) => {
    return await fetchWrapper(
      `${API_URL}/${url}`,
      {
        method,
        body: payload ? JSON.stringify(payload) : undefined,
        headers: {
          'Content-Type': 'application/json',
          ...(headers || {}),
        },
        credentials: 'include',
      },
      `custom ${method.toUpperCase()}`,
    );
  },

  getApiUrl: () => API_URL,
  // Optional methods:
  // getMany: () => { /* ... */ },
  // createMany: () => { /* ... */ },
  // deleteMany: () => { /* ... */ },
  // updateMany: () => { /* ... */ },
};

// Track request attempts to avoid logging errors that will be retried by React Query
const requestAttempts = new Map<string, { count: number; lastAttempt: number }>();

// Clean up old entries every 60 seconds
setInterval(() => {
  const now = Date.now();
  for (const [key, value] of requestAttempts.entries()) {
    if (now - value.lastAttempt > 60000) {
      requestAttempts.delete(key);
    }
  }
}, 60000);

async function fetchWrapper(
  url: string,
  init: RequestInit = {},
  operation = 'undefined',
  { timeoutMs = 25000 }: { timeoutMs?: number } = {},
) {
  const started = performance.now();
  const reqId = Math.random().toString(16).slice(2) + '-' + Date.now().toString(36);
  const hdrs = new Headers(init.headers || {});
  hdrs.set('x-af-cid', reqId);
  const opts: RequestInit = {
    ...init,
    headers: hdrs,
    mode: init.mode ?? 'cors',
    credentials: init.credentials ?? 'include',
  };

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(`timeout ${timeoutMs}ms`), timeoutMs);
  opts.signal = controller.signal;

  // Create a key to track this request
  const requestKey = `${operation}:${url}:${init.method ?? 'GET'}`;

  try {
    const res = await fetch(url, opts);
    clearTimeout(t);

    const attemptCount = incrementRequestCount(requestKey);

    const result = await handleResponse(res, url.toString(), operation, attemptCount);

    requestAttempts.delete(requestKey);

    return result;
  } catch (err) {
    if (err instanceof UnauthenticatedError) {
      throw err;
    }

    // If it's an ApiError, it was already logged by handleResponse, so just rethrow
    if (err instanceof ApiError) {
      throw err;
    }

    clearTimeout(t);

    const attemptCount = incrementRequestCount(requestKey);

    const duration = performance.now() - started;
    const errorName = (err as { name?: string })?.name ?? 'Unknown';
    const errorMessage = (err as { message?: string })?.message ?? 'Unknown error';

    const fullErrorMessage = `Fetch error for ${operation} on ${url.toString()}: ${errorMessage}`;

    // Only suppress logging for the generic "Failed to fetch" error which is often transient
    // All other errors should be logged immediately as they might be more specific/important
    const isGenericFailedToFetch = errorName === 'TypeError' && errorMessage === 'Failed to fetch';

    // For generic "Failed to fetch", only log after 3 attempts (when React Query exhausts retries)
    // For all other errors, log immediately
    const shouldLog = !isGenericFailedToFetch || attemptCount >= 3;
    const shouldLogInfo = shouldLog
      ? 'Should log'
      : `Should not log (attempts: ${attemptCount} < 3)`;

    // Log to console for debugging (don't use Logger - Sentry.withScope below handles Sentry reporting with more context)
    if (Config.isDev()) {
      // eslint-disable-next-line no-console
      console.error(`${fullErrorMessage} - ${shouldLogInfo}`, err, {
        url: url.toString(),
        operation,
        duration_ms: Math.round(duration),
        timeout_ms: timeoutMs,
        attempt_count: attemptCount,
      });
    }

    // Log to Sentry with full context (only for network/fetch errors, not HTTP errors)
    Sentry.withScope(scope => {
      scope.setTag('request_id', reqId);
      scope.setTag('operation', operation);
      scope.setTag('attempt_count', attemptCount.toString());
      scope.setContext('fetch', {
        url: url.toString(),
        method: opts.method ?? 'No method in opts',
        mode: opts.mode,
        credentials: opts.credentials,
        duration_ms: Math.round(duration),
        timeout_ms: timeoutMs,
        errorMessage: errorMessage,
        attemptCount: attemptCount,
      });
      Sentry.captureException(new Error(fullErrorMessage));
    });

    throw err;
  }
}

function incrementRequestCount(requestKey: string) {
  const now = Date.now();
  const attemptData = requestAttempts.get(requestKey);
  const attemptCount = attemptData ? attemptData.count + 1 : 1;
  requestAttempts.set(requestKey, { count: attemptCount, lastAttempt: now });
  return attemptCount;
}

class UnauthenticatedError extends Error {
  constructor() {
    super('401 custom error to trigger login redirect');
  }
}

async function handleResponse(
  response: Response,
  resource: string,
  operation: string,
  attemptCount: number,
) {
  if (response.status === 401) {
    if (!response.url?.includes('localhost') && !response.url?.includes('afternoon')) {
      // Adding this more for an informative log in case we have a misconfiguration
      // The check is not very robust. However, it should do the job for now.
      // Keeping the same behaviour in this scenario but logging an error, so we get alerted if it happens
      Logger.error(
        `Received 401 from unexpected URL: ${response.url}`,
        new Error('401 from unexpected URL'),
      );
    }
    await redirectToLogin();
    throw new UnauthenticatedError();
  }

  if (response.status === 428) {
    const data = await response.json();
    if (data.code === 'CREDENTIALS_REQUIRED') {
      window.location.replace('/invite');
      throw new Error('Credentials required - redirecting to complete setup');
    }
  }

  if (response.status === 204) {
    return { data: null };
  }

  if (response.status < 200 || response.status > 299) {
    const apiError = await ApiError.fromApiResponse(response);

    const errorDetails = apiError.errorId
      ? ` [error_id: ${apiError.errorId}${apiError.errorType ? `, type: ${apiError.errorType}` : ''}]`
      : '';
    const errorMessage = `HTTP ${response.status} error for ${operation} on ${resource}: ${apiError.message}${errorDetails}`;

    // Report all HTTP errors (4xx and 5xx) to capture frontend perspective
    // Backend errors will also be reported from backend with same error_id for correlation
    // This gives us both perspectives: backend stack trace + frontend user context
    // (beforeSend will filter 401s)
    Sentry.withScope(scope => {
      scope.setTag('operation', operation);
      scope.setTag('status', response.status);
      scope.setTag('attempt_count', attemptCount.toString());
      if (apiError.errorId) {
        scope.setTag('error_id', apiError.errorId);
      }
      if (apiError.errorType) {
        scope.setTag('error_type', apiError.errorType);
      }
      scope.setContext('response', {
        resource,
        status: response.status,
        message: apiError.message,
        attemptCount: attemptCount,
      });

      Sentry.captureException(new Error(errorMessage));
    });

    throw apiError;
  }

  try {
    const data = await response.json();
    return { data };
  } catch (error) {
    Logger.error('Failed to retrieve response JSON', error, {
      resource,
      operation,
      body: await getResponseBodyAsText(response),
    });
    throw new Error('Failed to retrieve response data');
  }
}

async function getResponseBodyAsText(response: Response) {
  try {
    return await response.text();
  } catch (e) {
    Logger.error('Could not read response body as text', e, {
      status: response.status,
      url: response.url,
      unparsedBody: response.body,
    });
    return 'Could not read response body as text';
  }
}
